import {
  AscResponse,
  AuthLoginResponsePayload,
  AuthSignupQuestionsRequestPayload,
  AuthSignupRequestPayload,
  AuthSignupResponsePayload,
  AuthShibbolethCheckResponsePayload,
} from '../../models';
import { httpService } from '../../utilities';
import axios, { AxiosHeaders, AxiosRequestTransformer } from 'axios';
import { AuthEndpoints } from './auth.endpoints';
import { SHARED_ROUTES } from '../../constants/shared.routes';
import { UserEndpoints } from '../user/user.endpoints';

export async function loginApi(email: string, password: string) {
  return httpService
    .post<AscResponse<AuthLoginResponsePayload>>(AuthEndpoints.login, {
      email,
      password,
    })
    .then((res) => res.data);
}

export async function shibbolethCheckApi(email: string) {
  return httpService
    .post<AuthShibbolethCheckResponsePayload>(AuthEndpoints.checkShibboleth, {
      email,
    })
    .then((res) => res.data);
}

//queries: 'code' | 'scope' | 'authuser' | 'hd' | 'prompt'
export async function googleAuthApi(
  type: 'signup' | 'signin',
  query: Record<string, string>
) {
  const queryString = new URLSearchParams(query)?.toString();
  return httpService
    .get<AscResponse<AuthLoginResponsePayload>>(
      `${
        type === 'signup'
          ? AuthEndpoints.googleAuthSignupCallback
          : AuthEndpoints.googleAuthSigninCallback
      }?${queryString}`
    )
    ?.then((res) => res?.data?.data);
}

export async function refreshTokenApi(refreshToken: string) {
  //Intentionally using axios here because we don't want to use the httpService
  return axios
    .post<AscResponse<AuthLoginResponsePayload>>(AuthEndpoints.refreshToken, {
      refreshToken,
    })
    .then((res) => res.data);
}

export async function signupApi(request: AuthSignupRequestPayload) {
  return httpService
    .post<AuthSignupResponsePayload>(AuthEndpoints.signup, request, {
      transformRequest: [
        (data: AuthSignupRequestPayload, headers: AxiosHeaders) => {
          headers[
            'x-requested-with'
          ] = `${process.env['NEXT_PUBLIC_PLATFORM_URL']}${SHARED_ROUTES.signup_verification}`;
          return data;
        },
      ].concat(axios.defaults.transformRequest as AxiosRequestTransformer),
    })
    .then((res) => res.data?.data);
}

export async function signupQuestionsApi(
  request: AuthSignupQuestionsRequestPayload
) {
  return httpService
    .post(AuthEndpoints.signupQuestions, request)
    .then((res) => res.data);
}

export async function verifyApi(id: string) {
  return httpService
    .get<AscResponse<AuthLoginResponsePayload>>(AuthEndpoints.verify + `/${id}`)
    .then((res) => res.data);
}

export async function resendVerificationEmailApi(email: string) {
  return httpService
    .get<AuthSignupResponsePayload>(
      AuthEndpoints.resendVerificationEmail +
        `?email=${encodeURIComponent(email.replace(/\+/g, '%2B'))}`,
      {
        transformRequest: [
          (data: { email: string }, headers: AxiosHeaders) => {
            headers[
              'x-requested-with'
            ] = `${process.env['NEXT_PUBLIC_PLATFORM_URL']}${SHARED_ROUTES.signupCreatePassword}`;
            return data;
          },
        ].concat(axios.defaults.transformRequest as AxiosRequestTransformer),
      }
    )
    .then((res) => res.data?.data);
}

export async function resetPasswordApi(
  resetToken: string,
  resetPasswordPayload: Record<
    'userEmail' | 'password' | 'confirmPassword',
    string
  >
) {
  return httpService
    .post<AscResponse<{ recoveryMessage: boolean }>>(
      `${AuthEndpoints.resetPassword}/${resetToken}`,
      resetPasswordPayload
    )
    .then((res) => res?.data?.data);
}

export async function createPasswordApi(
  verifyId: string,
  createPasswordPayload: Record<
    'userEmail' | 'password' | 'confirmPassword',
    string
  >
) {
  return httpService
    .post<AscResponse<AuthLoginResponsePayload>>(
      `${AuthEndpoints.createPassword}/${verifyId}`,
      createPasswordPayload
    )
    .then((res) => res?.data?.data);
}

export async function changePasswordOnSignupApi(newPassword: string) {
  return httpService
    .patch(`${UserEndpoints.changePassword}?type=reset`, { newPassword })
    .then((res) => res?.data?.data);
}

export async function forgotPasswordApi(userEmail: string) {
  return httpService
    .post<AscResponse<{ recoveryMessage: boolean }>>(
      AuthEndpoints.forgotPassword,
      {
        userEmail,
      },
      {
        transformRequest: [
          (data: { userEmail: string }, headers: AxiosHeaders) => {
            headers[
              'x-requested-with'
            ] = `${process.env['NEXT_PUBLIC_PLATFORM_URL']}${SHARED_ROUTES.resetPassword}`;
            return data;
          },
        ].concat(axios.defaults.transformRequest as AxiosRequestTransformer),
      }
    )
    .then((res) => res?.data?.data);
}
